import { Controller } from "@hotwired/stimulus"

// Types a scripted terminal session when the element scrolls into view.
// Line types: "cmd" (typed after a green $ prompt), "out" (muted output),
// "ok" (green signal output). Reduced motion renders the final state instantly.
export default class extends Controller {
  static values = { lines: Array }

  connect() {
    this.screen = document.createElement("div")
    this.element.appendChild(this.screen)

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    if (reduced || !("IntersectionObserver" in window)) {
      this.renderAll()
      return
    }

    this.observer = new IntersectionObserver(entries => {
      if (entries.some(e => e.isIntersecting)) {
        this.observer.disconnect()
        this.play()
      }
    }, { threshold: 0.4 })
    this.observer.observe(this.element)
  }

  disconnect() {
    this.observer?.disconnect()
    this.stopped = true
  }

  renderAll() {
    this.linesValue.forEach(line => this.screen.appendChild(this.buildLine(line, line.x)))
  }

  async play() {
    for (const line of this.linesValue) {
      if (this.stopped) return

      if (line.t === "cmd") {
        const row = this.buildLine(line, "")
        const textSpan = row.querySelector("[data-text]")
        this.screen.appendChild(row)
        this.moveCaret(textSpan)
        await this.wait(500)
        for (const char of line.x) {
          if (this.stopped) return
          textSpan.textContent += char
          this.moveCaret(textSpan)
          await this.wait(28 + Math.sin(textSpan.textContent.length * 7) * 14)
        }
        await this.wait(320)
      } else {
        this.screen.appendChild(this.buildLine(line, line.x))
        this.caret?.remove()
        await this.wait(line.t === "ok" ? 260 : 110)
      }
    }
    // Rest state: fresh prompt with a blinking caret
    const rest = this.buildLine({ t: "cmd" }, "")
    this.screen.appendChild(rest)
    this.moveCaret(rest.querySelector("[data-text]"))
  }

  buildLine(line, text) {
    const row = document.createElement("div")
    row.className = "whitespace-pre-wrap break-words"

    if (line.t === "cmd") {
      const prompt = document.createElement("span")
      prompt.className = "text-highlight"
      prompt.textContent = "$ "
      row.appendChild(prompt)
      const span = document.createElement("span")
      span.className = "text-text"
      span.setAttribute("data-text", "")
      span.textContent = text
      row.appendChild(span)
    } else {
      const span = document.createElement("span")
      span.className = line.t === "ok" ? "text-highlight" : "text-text-muted"
      span.textContent = text
      row.appendChild(span)
    }
    return row
  }

  moveCaret(afterEl) {
    if (!this.caret) {
      this.caret = document.createElement("span")
      this.caret.className = "terminal-caret"
      this.caret.setAttribute("aria-hidden", "true")
    }
    afterEl.after(this.caret)
  }

  wait(ms) {
    return new Promise(resolve => setTimeout(resolve, ms))
  }
}
